import type {
  CustomField,
  ID,
  Geofence as SitumGeofence,
  UUID,
} from "@situm/sdk-js";
import { area } from "@turf/area";
import { centerOfMass } from "@turf/center-of-mass";
import { polygon } from "@turf/helpers";
import { isEqual } from "lodash-es";

import {
  type GeofenceOverlayFeature,
  type OverlayFeatureCollection,
  OverlayFeatureTypes,
} from "@/features/editor/types";
import { dateToTimestamp, timestampToDate } from "@/features/utils/date";
import { swapLatLngCoordinates, toFixedDecimalsCoordinate } from "@/libs/geo";
import { CustomFields } from "@/libs/models";

/****************************************************************************/
/* Types */
/****************************************************************************/

// Properties of the Geofence class, extended from the @situm/sdk-js Geofence
type GeofenceProperties = SitumGeofence & {
  center?: [number, number];
  area?: number;
};

// Properties of the geojson overlay feature
export interface GeofenceGeojsonProperties
  extends Omit<
    GeofenceProperties,
    "geometric" | "type" | "createdAt" | "updatedAt"
  > {
  type: OverlayFeatureTypes;
  createdAt?: EpochTimeStamp;
  updatedAt?: EpochTimeStamp;
}

/****************************************************************************/
/* Mappers & utils */
/****************************************************************************/
export function polygonCenterMapper(data: Coordinate[][]): Coordinate2D {
  const centerData = centerOfMass(polygon(data));
  return [
    centerData.geometry.coordinates[0],
    centerData.geometry.coordinates[1],
  ];
}
export function polygonAreaMapper(data: Coordinate[][]): number {
  return area(polygon(data));
}

/****************************************************************************/
/* Geofence class */
/****************************************************************************/
export class Geofence implements GeofenceProperties {
  buildingId: string;
  code?: string;
  customFields?: CustomField[];
  floorId: ID;
  geometric: [number, number][];
  info?: string;
  name: string;
  organizationId?: string;
  id: string;
  createdAt: Date;
  updatedAt: Date;
  deleted: boolean;
  type: "POLYGON";

  center: [number, number];
  area: number;

  constructor(public geofence: SitumGeofence) {
    Object.assign(this, geofence);
    this.geometric = toFixedDecimalsCoordinate(this.geometric, 9);
    this.createdAt = geofence.createdAt
      ? new Date(geofence.createdAt)
      : undefined;
    this.updatedAt = geofence.updatedAt
      ? new Date(geofence.updatedAt)
      : undefined;
    // turf needs data as [lng,lat]
    this.area = polygonAreaMapper([swapLatLngCoordinates(this.geometric)]);
    // turf needs data as [lng,lat] and also returns data as [lng, lat]
    this.center = swapLatLngCoordinates(
      polygonCenterMapper([swapLatLngCoordinates(this.geometric)]),
    );
  }

  get cf() {
    return new CustomFields(this.customFields);
  }

  isEqual(other: Geofence) {
    return isEqual(this, other);
  }

  static fromGeojson(feature: GeofenceOverlayFeature) {
    const geofence: Required<GeofenceProperties> = {
      area: feature.properties.area,
      buildingId: feature.properties.buildingId,
      // Its standard in GeoJSON to use [lng,lat] for coordinates
      center: swapLatLngCoordinates(feature.properties.center),
      code: feature.properties.code,
      createdAt: feature.properties.createdAt
        ? timestampToDate(feature.properties.createdAt)
        : undefined,
      customFields: feature.properties.customFields?.filter(
        (cf) => cf.key !== "",
      ),
      deleted: feature.properties.deleted,
      floorId: feature.properties.floorId,
      geometric: swapLatLngCoordinates(feature.geometry.coordinates[0]) as [
        number,
        number,
      ][],
      id: feature.properties.id,
      info: feature.properties.info,
      name: feature.properties.name,
      organizationId: feature.properties.organizationId,
      type: "POLYGON",
      updatedAt: feature.properties.updatedAt
        ? timestampToDate(feature.properties.updatedAt)
        : undefined,
    };

    return new Geofence(geofence);
  }

  toGeojson(): GeofenceOverlayFeature {
    const properties: Required<GeofenceGeojsonProperties> = {
      area: this.area,
      buildingId: this.buildingId,
      // Its standard in GeoJSON to use [lng,lat] for coordinates
      center: swapLatLngCoordinates(this.center),
      code: this.code,
      createdAt: this.createdAt ? dateToTimestamp(this.createdAt) : undefined,
      customFields: this.customFields ?? [],
      deleted: this.deleted,
      floorId: this.floorId,
      id: this.id,
      info: this.info,
      name: this.name,
      organizationId: this.organizationId,
      type: OverlayFeatureTypes.geofence,
      updatedAt: this.updatedAt ? dateToTimestamp(this.updatedAt) : undefined,
    };

    return {
      geometry: {
        coordinates: [swapLatLngCoordinates(this.geometric)],
        type: "Polygon",
      },
      id: `geofence_${this.id}`,
      properties: properties,
      type: "Feature",
    };
  }
}

/****************************************************************************/
/* Geofences class */
/****************************************************************************/
export class Geofences {
  constructor(public geofences: Geofence[]) {
    this.geofences = geofences;
  }

  static fromGeofenceOverlayFeatures(features: GeofenceOverlayFeature[]) {
    return new Geofences(features.map(Geofence.fromGeojson));
  }

  get(id: UUID): Geofence | undefined {
    return this.geofences.find((fence) => fence.id == id);
  }

  filterByFloorId(floorId: ID): Geofences {
    const newGeofences = this.geofences.filter(
      (fence) => fence.floorId == floorId,
    );
    return new Geofences(newGeofences);
  }

  toArray(): Geofence[] {
    return this.geofences;
  }

  toGeojson(): OverlayFeatureCollection<GeofenceOverlayFeature> {
    return {
      features: this.geofences.map((g) => g.toGeojson()),
      type: "FeatureCollection",
    };
  }
}
